import Environment from '../../environment';
import { auth } from '../../firebase';

const API_URL = Environment.API_URL;
const FLASK_HOST_URL = Environment.FLASK_HOST_URL;
const CLIENT_BACKEND_URL = Environment.CLIENT_BACKEND_URL;

type ApiConfig = {
  baseUrl?: string;
  headers?: Record<string, string>;
};

type RequestOptions = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  data?: any;
  config?: ApiConfig;
  influencerId?: string;
};

const defaultConfig: ApiConfig = {
  baseUrl: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
};

const flaskConfig: ApiConfig = {
  baseUrl: FLASK_HOST_URL,
  headers: {
    'Content-Type': 'application/json',
  }
};

const clientBackendConfig: ApiConfig = {
  baseUrl: CLIENT_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  }
};

export const apiRequest = async <T>({
  method,
  endpoint,
  data,
  config = defaultConfig,
  influencerId,
}: RequestOptions): Promise<T> => {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const headers = {
    ...config.headers,
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(influencerId && { 'X-Influencer-ID': influencerId }),
  };

  // Remove Content-Type for FormData
  if (data instanceof FormData) {
    delete headers['Content-Type'];
  }

  const url = `${config.baseUrl}${endpoint}`;
  

  const requestOptions: RequestInit = {
    method,
    headers,
    body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined
  };

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    let errorMessage = `API request failed: ${response.statusText}`;
    const errorExtras: { code?: string; goLive?: { start: string; end: string }; deadline?: string; min?: string; max?: string } = {};
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
      if (typeof errorData.code === 'string') errorExtras.code = errorData.code;
      const goLive = errorData.goLive;
      if (typeof goLive?.start === 'string' && typeof goLive?.end === 'string') errorExtras.goLive = { start: goLive.start, end: goLive.end };
      for (const key of ['deadline', 'min', 'max'] as const) {
        if (typeof errorData[key] === 'string') errorExtras[key] = errorData[key];
      }
    } catch {
      // If response is not JSON, use default error message
    }
    console.error('API Error:', {
      status: response.status,
      statusText: response.statusText,
      error: errorMessage
    });
    // `status` lets callers tell a refusal (403) from a transient failure; `code` and its fields
    // (`goLive`, `deadline`, `min`, `max`) carry machine-readable refusals (e.g. GO_LIVE_CHANGED).
    throw Object.assign(new Error(errorMessage), { status: response.status, ...errorExtras });
  }

  // Return null for 204 No Content responses
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
};

export const makeFlaskRequest = async <T>(options: Omit<RequestOptions, 'config'>): Promise<T> => {
  return apiRequest<T>({ ...options, config: flaskConfig });
};

export const makeClientBackendRequest = async <T>(options: Omit<RequestOptions, 'config'>): Promise<T> => {
  return apiRequest<T>({ ...options, config: clientBackendConfig });
}; 